import React,{useEffect,useRef,useState} from 'react';
import {AppState} from 'react-native';
import {View,Text,TextInput,TouchableOpacity,StyleSheet} from './rn';
import ProfileSection from './ProfileSection';
import {useTheme} from '../theme/ThemeContext';
import {useTranslation} from '../i18n/I18nContext';
import {api} from '../services/api';

export type VisitRecord={enabled:boolean;place:string|null;timing:string|null;revision:number};
export type VisitSnapshot={mine:VisitRecord;incoming:VisitRecord|null};

export default function VisitInvitation(props:{token:string;contactId:number;myId:number}) {
  // Parent remounts on identity/contact changes; no account's private wording
  // can flash in another account, even before the next effect is installed.
  return <VisitInvitationSession key={`${props.token}:${props.myId}:${props.contactId}`} {...props}/>;
}
function VisitInvitationSession({token,contactId}:{token:string;contactId:number;myId:number}) {
  const {t}=useTranslation();const colors=useTheme();
  const [snapshot,setSnapshot]=useState<VisitSnapshot|null>(null);
  const [error,setError]=useState(false),[busy,setBusy]=useState(false);
  const [editing,setEditing]=useState(false),[place,setPlace]=useState(''),[timing,setTiming]=useState('');
  const [revision,setRevision]=useState(0),[refresh,setRefresh]=useState(0);
  const generation=useRef(0),foreground=useRef(AppState.currentState==='active');
  const mounted=useRef(true),saving=useRef(false);
  useEffect(()=>()=>{mounted.current=false;generation.current++;},[]);
  useEffect(()=>{
    const epochRef=generation;
    let timer:ReturnType<typeof setTimeout>|undefined;
    const load=async()=>{
      const epoch=++generation.current;
      if(!foreground.current || saving.current)return;
      try {
        const data=await api.getVisitInvitations(token,contactId);
        if(mounted.current && epoch===generation.current && foreground.current){setSnapshot(data);setError(false);}
      }catch{
        if(mounted.current && epoch===generation.current){setSnapshot(null);setError(true);}
      }finally{
        if(mounted.current && epoch===generation.current && foreground.current)timer=setTimeout(load,15000);
      }
    };
    load();
    const sub=AppState.addEventListener('change',state=>{
      foreground.current=state==='active';generation.current++;clearTimeout(timer);
      setSnapshot(null);setEditing(false);
      if(foreground.current)load();
    });
    return()=>{epochRef.current++;clearTimeout(timer);sub.remove();};
  },[token,contactId,refresh]);
  const begin=()=>{
    if(!snapshot)return;
    setPlace(snapshot.mine.place??t('visit.defaultPlace'));setTiming(snapshot.mine.timing??t('visit.defaultTime'));
    setRevision(snapshot.mine.revision);setEditing(true);
  };
  const save=async(enabled:boolean)=>{
    if(!snapshot || saving.current)return;
    saving.current=true;setBusy(true);generation.current++;
    try {
      await api.saveVisitInvitation(token,contactId,enabled
        ? {enabled:true,revision,place:place.trim(),timing:timing.trim()}
        : {enabled:false,revision:snapshot.mine.revision});
      if(mounted.current){setEditing(false);setSnapshot(null);}
    }catch{
      // An ambiguous response must be reconciled by GET, never retried as PUT.
      if(mounted.current){setEditing(false);setSnapshot(null);setError(true);}
    }finally{
      saving.current=false;
      if(mounted.current){setBusy(false);setRefresh(n=>n+1);}
    }
  };
  const text={color:colors.black,fontSize:16,flexShrink:1};
  const action=(label:string,id:string,press:()=>void,disabled=false)=><TouchableOpacity
    testID={id} accessibilityRole="button" accessibilityState={{disabled}} disabled={disabled}
    onPress={press} style={[styles.action,disabled&&styles.disabled]}>
    <Text style={text}>{label}</Text></TouchableOpacity>;
  return <ProfileSection testID="visit-invitation" header={<Text style={text}>{t('visit.title')}</Text>}>
    {snapshot?.incoming?.enabled && <View testID="visit-incoming" style={styles.incoming}>
      <Text style={text}>{t('visit.incoming')}</Text>
      <Text style={text}>{t('visit.place')}: {snapshot.incoming.place}</Text>
      <Text style={text}>{t('visit.time')}: {snapshot.incoming.timing}</Text>
    </View>}
    <Text style={text}>{t('visit.hint')}</Text>
    {error && <><Text accessibilityLiveRegion="polite" style={text}>{t('visit.unavailable')}</Text>
      {action(t('common.retry'),'visit-retry',()=>setRefresh(n=>n+1),busy)}</>}
    {snapshot && !editing && <>
      <Text testID="visit-own-state" style={[text,styles.state]}>{snapshot.mine.enabled?t('visit.enabled'):t('visit.off')}</Text>
      {snapshot.mine.enabled && <><Text style={text}>{t('visit.place')}: {snapshot.mine.place}</Text>
        <Text style={text}>{t('visit.time')}: {snapshot.mine.timing}</Text></>}
      {action(t('visit.edit'),'visit-edit',begin,busy)}
      {snapshot.mine.enabled && action(t('visit.revoke'),'visit-revoke',()=>{save(false);},busy)}
    </>}
    {editing && snapshot && <View style={styles.editor}>
      <Text style={text}>{t('visit.place')}</Text>
      <TextInput testID="visit-place" accessibilityLabel={t('visit.place')} value={place} onChangeText={setPlace}
        maxLength={160} editable={!busy} multiline style={[text,styles.input,{borderColor:colors.gray200}]}/>
      <Text style={text}>{t('visit.time')}</Text>
      <TextInput testID="visit-time" accessibilityLabel={t('visit.time')} value={timing} onChangeText={setTiming}
        maxLength={160} editable={!busy} multiline style={[text,styles.input,{borderColor:colors.gray200}]}/>
      {action(t('visit.save'),'visit-save',()=>{save(true);},busy||!place.trim()||!timing.trim())}
      {action(t('common.cancel'),'visit-cancel',()=>setEditing(false),busy)}
    </View>}
  </ProfileSection>;
}
const styles=StyleSheet.create({
  action:{minHeight:48,justifyContent:'center'},disabled:{opacity:0.5},
  incoming:{gap:8,marginBottom:16},state:{marginTop:12},editor:{gap:8,marginTop:16},
  input:{minHeight:48,borderWidth:1,padding:10},
});
